/* ============================================================
   STATEMENT LINE GLOSSARY
   Rich-tooltip metadata for Income Statement, Balance Sheet,
   Cash Flow lines rendered by FormalStatementTable.

   Keyed by normalized label (lowercase, collapsed whitespace).
   Returns { title, formula?, hint?, explanation?, direction?, betterWhen? }.

   All fields optional except the title (which we derive from the label
   if absent). Tooltip renders gracefully when fields are missing.

   Scope note: this is the SAME shape that ratio tooltips use in
   MetricHorizonTable's RichExplanation. Stays small on purpose; add
   entries as you find lines in the wild that deserve explanation.
   ============================================================ */

function normalizeKey(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    // Collapse em-dash (—), en-dash (–), and minus-sign (−) to ascii hyphen.
    .replace(/[—–−]/g, '-')
    .trim();
}

/* Every entry is keyed by the normalized label. Aliases share one entry
   by listing multiple keys pointing at the same object via the spread
   helper below. */
const ENTRIES = {
  /* ── Income Statement ────────────────────────────────────────── */
  'primary income (gross)': {
    formula: 'Monthly gross × months in period',
    hint: 'Primary revenue stream.',
    explanation: 'Gross primary income before withholding tax and statutory contributions. Taxed items flow separately below.',
    direction: { whenUp: 'More earned income.', whenDown: 'Drop in earned income.' },
    betterWhen: 'higher',
  },
  'recurring revenue': {
    formula: 'Σ active recurring revenue',
    hint: 'Monthly recurring revenue from the recurring stream.',
    explanation: 'Recurring revenue, before discounts or refunds. Independent of primary income.',
    direction: { whenUp: 'Subscribers + retention winning.', whenDown: 'Churn or pricing pressure.' },
    betterWhen: 'higher',
  },
  'support contrib / ar': {
    hint: 'Contribution AR balance shifts.',
    explanation: 'Contribution-channel accounts receivable movement across the period. Positive = more receivable.',
    direction: { whenUp: 'AR growing (less collected).', whenDown: 'Collected / settled.' },
    betterWhen: 'neutral',
  },
  'contribution ar': {
    hint: 'Contribution AR balance shifts.',
    explanation: 'Contribution-channel accounts receivable movement across the period. Positive = more receivable.',
    direction: { whenUp: 'AR growing (less collected).', whenDown: 'Collected / settled.' },
    betterWhen: 'neutral',
  },
  'total revenue': {
    formula: 'Primary income + recurring revenue + contribution AR',
    hint: 'Top line.',
    explanation: 'All revenue sources combined. Lens adds primary income, recurring revenue, and contribution-receivable movement.',
    direction: { whenUp: 'Top line expanding.', whenDown: 'Top line contracting.' },
    betterWhen: 'higher',
  },

  'subscriptions (recurring)': {
    formula: 'Σ monthly subscriptions',
    hint: 'SaaS + streaming + memberships.',
    explanation: 'Software, entertainment, club memberships. Recurring outflows managed in the Budgets piecewise editor.',
    direction: { whenUp: 'Subscription stack growing.', whenDown: 'Trimmed subscriptions.' },
    betterWhen: 'lower',
  },
  'support direct ap': {
    hint: 'Direct support spend.',
    explanation: 'Direct support payments. Distinct from the routed channel.',
    direction: { whenUp: 'Higher direct support AP.', whenDown: 'Lower direct support AP.' },
    betterWhen: 'neutral',
  },
  'support - routed channel': {
    hint: 'Support AP routed through a pooled channel.',
    explanation: 'Support payments routed through a pooled channel. Tracked separately so cadence remains tunable.',
    direction: { whenUp: 'More routed-channel outflow.', whenDown: 'Lower routed-channel outflow.' },
    betterWhen: 'neutral',
  },
  'fuel': {
    hint: 'Transportation fuel cost.',
    explanation: 'Fuel or transport-energy spend for vehicles. Sensitive to local price swings.',
    betterWhen: 'lower',
  },
  'seasonal events': {
    hint: 'Birthdays, holidays, milestones.',
    explanation: 'Periodic event spend (birthdays / anniversaries / holidays). Non-uniform by month.',
    betterWhen: 'neutral',
  },
  'lifestyle': {
    hint: 'Dining, entertainment, hobbies.',
    explanation: 'Discretionary lifestyle spending. First lever when tightening the budget.',
    betterWhen: 'lower',
  },
  'total operating expenses': {
    formula: 'Σ operating expense lines',
    hint: 'Operating cost base.',
    explanation: 'Sum of all operating expense categories before non-operating items (interest, tax, statutory contributions).',
    betterWhen: 'lower',
  },

  'mortgage interest': {
    hint: 'Interest portion of mortgage payments.',
    explanation: 'Interest component of mortgage payments. Principal flows through the Cash Flow Statement, not here.',
    betterWhen: 'lower',
  },
  'withholding tax': {
    hint: 'Employer-withheld income tax.',
    explanation: 'Withholding tax on primary income. Already deducted from gross where applicable; shown for transparency.',
    betterWhen: 'neutral',
  },
  'government contribs + fees': {
    hint: 'Statutory contributions and payment fees.',
    explanation: 'Statutory contributions and incidental bank/payment fees.',
    betterWhen: 'neutral',
  },
  'total non-operating': {
    formula: 'Interest + tax + statutory + fees',
    hint: 'Below-the-line outflow.',
    explanation: 'Non-operating costs: interest expense, taxes withheld, statutory contributions, fees.',
    betterWhen: 'lower',
  },

  'net income': {
    formula: 'Revenue − Operating − Non-operating',
    hint: 'Bottom line.',
    explanation: 'Net income for the period. Positive = accumulating equity; negative = drawing down reserves.',
    direction: { whenUp: 'Profitability improving.', whenDown: 'Profitability eroding.' },
    betterWhen: 'higher',
  },

  /* ── Balance Sheet ───────────────────────────────────────────── */
  'liquid assets - bank + cash + e-wallets': {
    hint: 'Immediately spendable money.',
    explanation: 'All bank, cash, and e-wallet balances classified as liquid (excludes maintaining balances and foreign-currency dust).',
    betterWhen: 'higher',
  },
  'fixed assets (net book value)': {
    formula: 'Cost basis − accumulated depreciation',
    hint: 'Property, equipment, vehicles.',
    explanation: 'NC-Assets at net book value (cost minus accumulated depreciation). Driven by Command simulations when enabled.',
    betterWhen: 'neutral',
  },
  'total assets': {
    formula: 'Liquid + fixed + other',
    hint: 'Assets controlled by the book.',
    explanation: 'Sum of all assets at book value. Balance-sheet left side.',
    betterWhen: 'higher',
  },

  'credit card debt (outstanding)': {
    hint: 'Revolving short-term balance.',
    explanation: 'Outstanding card, revolving, or similar short-term debt. Paying it down reduces near-term financing pressure and future cash outflow.',
    betterWhen: 'lower',
  },
  'short-term debt (outstanding)': {
    hint: 'Short-term balance owed.',
    explanation: 'Outstanding card, revolving, or similar short-term debt. Paying it down reduces near-term financing pressure and future cash outflow.',
    betterWhen: 'lower',
  },
  'credit card credits': {
    hint: 'Statement credits.',
    explanation: 'Credits on card or revolving statements (refunds, rewards). Effectively negative liabilities until redeemed.',
    betterWhen: 'neutral',
  },
  'short-term credits': {
    hint: 'Credits against short-term obligations.',
    explanation: 'Credits against card, revolving, or similar short-term obligations. Effectively negative liabilities until redeemed or applied.',
    betterWhen: 'neutral',
  },
  'long-term liabilities': {
    hint: 'Term debt + multi-year loans.',
    explanation: 'Long-term debt obligations such as property loans, vehicle loans, or multi-year notes. Repayment schedule tracked in the Settings tab.',
    betterWhen: 'lower',
  },
  'total liabilities': {
    formula: 'Short-term debt - credits + LTL',
    hint: 'Obligations owed by the book.',
    explanation: 'Sum of all liabilities. Balance-sheet right side (top portion).',
    betterWhen: 'lower',
  },

  'owner equity (proxy)': {
    hint: 'Equity proxy.',
    explanation: 'Proxy for owner\'s equity (retained contributions). Accounting proper uses a closing cascade; this is a simplified analytic view.',
    betterWhen: 'higher',
  },
  'suspense balance': {
    hint: 'Unreconciled plug.',
    explanation: 'Unreconciled balance-sheet plug — keeps the equation balanced when imports drift. Should trend toward zero.',
    betterWhen: 'lower',
  },
  'total equity': {
    formula: 'Owner equity + suspense',
    hint: 'Residual balance-sheet claim.',
    explanation: 'Equity total. Balance-sheet right side (bottom portion). Equals Assets − Liabilities.',
    betterWhen: 'higher',
  },
  'total liabilities + equity': {
    formula: 'Liabilities + equity = assets (identity)',
    hint: 'Balance-sheet identity.',
    explanation: 'The balance-sheet identity: liabilities + equity must equal total assets. Mismatch = reconciliation error.',
    betterWhen: 'neutral',
  },

  /* Balance Sheet — multi-period shape labels (shapeBalanceSheetMulti) */
  'cash + e-wallets': {
    hint: 'Liquid cash + digital wallets.',
    explanation: 'Cash in bank + e-wallet balances. Same concept as "Liquid assets" in the single-period view.',
    betterWhen: 'higher',
  },
  'custody receivable': {
    hint: 'Receivable held by a custodian.',
    explanation: 'Balances held by third parties, such as custody or escrow-style arrangements. Receivable because it is expected to come back to the book.',
    betterWhen: 'neutral',
  },
  'accounts receivable': {
    hint: 'Amounts owed to the book.',
    explanation: 'Outstanding amounts owed to the book, including contribution AR or trade receivables. Higher AR = more collection work ahead.',
    direction: { whenUp: 'More uncollected money owed.', whenDown: 'Collected / settled.' },
    betterWhen: 'neutral',
  },
  'fixed assets / real estate': {
    formula: 'Cost basis − accumulated depreciation',
    hint: 'Property, equipment, vehicles.',
    explanation: 'NC-Assets at net book value. Same concept as "NC-Assets (net book value)" in the single-period view.',
    betterWhen: 'neutral',
  },
  'credit cards payable': {
    hint: 'Short-term balance owed.',
    explanation: 'Outstanding card or revolving balances. Same concept as short-term debt outstanding in the single-period view.',
    betterWhen: 'lower',
  },
  'short-term debt payable': {
    hint: 'Short-term balance owed.',
    explanation: 'Outstanding card, revolving, or similar short-term balances. Same concept as short-term debt outstanding in the single-period view.',
    betterWhen: 'lower',
  },
  'accounts payable': {
    hint: 'Amounts the book owes others.',
    explanation: 'Outstanding bills across vendors and support channels. Higher AP means cash is being retained by delaying payment.',
    betterWhen: 'neutral',
  },
  'mortgages payable': {
    hint: 'Outstanding term-debt principal.',
    explanation: 'Term-debt balances owed. Reduces over time as principal is paid down in the Cash Flow Statement.',
    betterWhen: 'lower',
  },
  'total equity (a - l)': {
    formula: 'Total Assets − Total Liabilities',
    hint: 'Residual balance-sheet claim.',
    explanation: 'Equity computed by the accounting identity: Assets − Liabilities. Same concept as "Total equity" in the single-period view.',
    betterWhen: 'higher',
  },
  'total l + e': {
    formula: 'Liabilities + Equity = Assets',
    hint: 'Balance-sheet identity.',
    explanation: 'Liabilities + Equity must equal Total Assets. Same concept as "Total liabilities + equity" in the single-period view — rising/falling is mechanical, not good or bad.',
    betterWhen: 'neutral',
  },

  /* Cash Flow Statement — additional entries */
  'custody outflow': {
    hint: 'Capital movement to custody.',
    explanation: 'Cash moved into custody arrangements. Appears in CFI because it\'s a capital move, not operating spend.',
    betterWhen: 'neutral',
  },

  /* ── Cash Flow Statement ─────────────────────────────────────── */
  'δ accounts payable': {
    hint: 'Non-cash AP adjustment.',
    explanation: 'Change in accounts payable between periods. Added back in CFO because AP increase = cash retained (didn\'t pay yet).',
    betterWhen: 'neutral',
  },
  'net cash from operations (cfo)': {
    formula: 'Net income ± non-cash adjustments',
    hint: 'Cash generated by running the business.',
    explanation: 'Operating cash flow: net income adjusted for non-cash items (depreciation, AP/AR movement). Positive = business generates cash.',
    betterWhen: 'higher',
  },
  'custody outflow (reserve milestone)': {
    hint: 'One-time custody event.',
    explanation: 'Custody outflow for a reserve milestone. One-time — appears in CFI because it is a capital custody movement.',
    betterWhen: 'neutral',
  },
  'net cash from investing (cfi)': {
    formula: 'Σ investing outflows/inflows',
    hint: 'Capital moves.',
    explanation: 'Investing cash flow: purchases/sales of assets, custody events. Usually negative (you\'re investing cash into assets).',
    betterWhen: 'neutral',
  },

  'debt paydown': {
    hint: 'Principal paid down on short-term debt.',
    explanation: 'Cash applied to short-term principal. Reduces short-term debt on the Balance Sheet.',
    direction: { whenUp: 'Debt reducing faster.', whenDown: 'Debt reducing slower.' },
    betterWhen: 'higher',
  },
  'mortgage principal': {
    hint: 'Principal portion of mortgage payment.',
    explanation: 'Principal repayment on term debt. Reduces LTL on the Balance Sheet. Interest is on the Income Statement.',
    betterWhen: 'higher',
  },
  'loan inflow': {
    hint: 'Loan proceeds received.',
    explanation: 'Inflow from a new loan tranche. Increases cash and creates a matching liability.',
    betterWhen: 'neutral',
  },
  'loan repayment': {
    hint: 'Cash outflow repaying loan principal.',
    explanation: 'Outflow repaying loan principal per schedule.',
    betterWhen: 'neutral',
  },
  'net cash from financing (cff)': {
    formula: 'Σ debt paydown/proceeds + owner moves',
    hint: 'Financing side.',
    explanation: 'Financing cash flow: debt paydowns, new debt, owner contributions/distributions.',
    betterWhen: 'neutral',
  },
  'net change in cash': {
    formula: 'CFO + CFI + CFF',
    hint: 'Bottom line of the cash flow statement.',
    explanation: 'Net movement in cash for the period. Equals the change in liquid assets on the Balance Sheet.',
    direction: { whenUp: 'Cash position growing.', whenDown: 'Cash position shrinking.' },
    betterWhen: 'higher',
  },
};

/* Alias block: some labels appear in single-period shapes with different
   wording than multi-period shapes. Keep one canonical entry + alias it. */
const ALIASES = {
  'support — routed channel':        'support - routed channel',
};

export function lookupLineGlossary(label) {
  if (!label) return null;
  const key = normalizeKey(label);
  const aliased = ALIASES[key] ?? key;
  return ENTRIES[aliased] ?? null;
}
